import React, { useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {getImage, setImage} from '../../utils/idbHelpers';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    IconButton,
    Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';

const ItemTypes = {
    THIS_THEN: 'thisThen',
};

// Styled components
const StyledCard = styled(Card)(({ theme, isSelected }) => ({
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.2s ease',
    borderRadius: 4,
    borderTop: '1px solid var(--border-color)',
    borderRight: '1px solid var(--border-color)',
    borderBottom: '1px solid var(--border-color)',
    backgroundColor: isSelected ? 'var(--light-background)' : 'var(--background-color)',
    '&:hover': {
        backgroundColor: 'var(--light-background)',
        '& .delete-button': {
            opacity: 1,
        }
    }
}));

const DeleteButton = styled(IconButton)({
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 4,
    backgroundColor: 'var(--danger-color)',
    color: 'white',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 10,
    '&:hover': {
        backgroundColor: 'var(--danger-color)',
        filter: 'brightness(0.9)',
    },
    '& svg': {
        fontSize: '0.875rem',
    }
});

const ThumbnailContainer = styled(Box)({
    display: 'flex',
    gap: 4,
    justifyContent: 'center',
    marginTop: 8,
    overflow: 'hidden',
});

const ImageThumbnail = styled('img')({
    width: 32,
    height: 32,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid var(--border-color)',
});

const Placeholder = styled(Box)({
    width: 32,
    height: 32,
    border: `1px dashed var(--border-color)`,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const DraggableThisThenItem = ({ item, index, moveItem, onSelect, isSelected, projectData, BASE_URL, token, setImage, onDelete, projectId }) => {
    const [, ref] = useDrag({
        type: ItemTypes.THIS_THEN,
        item: { index },
    });

    const [, drop] = useDrop({
        accept: ItemTypes.THIS_THEN,
        hover: (draggedItem) => {
            if (draggedItem.index !== index) {
                moveItem(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    const [isHovered, setIsHovered] = useState();
    const [thisImages, setThisImages] = useState([]);
    const [thenImages, setThenImages] = useState([]);

    const ensureImageObject = (img) => {
        if (!img) {
            return {
                id: '',
                altName: '',
                name: '',
                localPath: '',
                url: ''
            };
        }
        if (typeof img === 'string') {
            return {
                id: img,
                altName: '',
                name: '',
                localPath: '',
                url: ''
            };
        }
        return {
            id: img.id || img._id || '',
            altName: img.altName || '',
            name: img.name || '',
            localPath: img.localPath || '',
            url: img.url || '',
            ...img // preserve any additional fields
        };
    };

    useEffect(() => {
        const fetchImages = async (images) => {
            if (!Array.isArray(images)) {
                console.warn('Images is not an array:', images);
                return [];
            }

            return await Promise.all(
                images.map(async (imageObj) => {
                    try {
                        // Ensure we have a proper image object
                        const normalizedImg = ensureImageObject(imageObj);
                        const imageId = normalizedImg.id;

                        const imageDetails = projectData?.project?.data?.images?.find(
                            image => image._id === imageId
                        );

                        if (!imageDetails) {
                            console.warn(`Image details not found for ID: ${imageId}`);
                            return null;
                        }

                        let cachedImage = await getImage(projectId, imageDetails.name);

                        if (cachedImage) {
                            return {
                                ...normalizedImg,
                                ...imageDetails,
                                imageUrl: cachedImage
                            };
                        }

                        const response = await fetch(
                            `${BASE_URL}/project/${projectData.project._id}/image/${imageDetails.name}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                },
                            }
                        );

                        if (!response.ok) {
                            throw new Error(`Failed to fetch image: ${response.statusText}`);
                        }

                        const data = await response.json();
                        await setImage(projectId, imageDetails.name, data.imageUrl);

                        return {
                            ...normalizedImg,
                            ...imageDetails,
                            imageUrl: data.imageUrl
                        };
                    } catch (error) {
                        console.error('Error processing image:', error);
                        return null;
                    }
                })
            );
        };


        fetchImages(item.this.images).then(setThisImages);
        fetchImages(item.then.images).then(setThenImages);
    }, [item.this.images, item.then.images, projectData, BASE_URL, token, setImage]);

    const handleDelete = (e) => {
        e.stopPropagation();
        if (window.confirm("Are you sure you want to delete this item?")) {
            onDelete(item._id);
        }
    };

    return (
        <StyledCard
            ref={(node) => ref(drop(node))}
            isSelected={isSelected}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onSelect(item._id)}
            elevation={isSelected ? 2 : 1}
            sx={{ 
                width: '100%',
                mb: 0.5,
                borderLeft: isSelected ? '4px solid var(--primary-color)' : '4px solid transparent',
                transition: 'all 0.2s ease'
            }}
        >
            <CardContent sx={{ p: 1, pb: '8px !important', position: 'relative' }}>
                <Typography 
                    variant="body2" 
                    noWrap
                    title={item?.name || 'Unnamed'}
                    sx={{ 
                        fontWeight: isSelected ? 'bold' : 'medium',
                        color: isSelected ? 'var(--primary-color)' : 'var(--text-color)',
                        pr: 3 // Make room for delete button
                    }}
                >
                    {item?.name || 'Unnamed'}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        flex: 1,
                        overflow: 'hidden'
                    }}>
                        {(!thisImages || thisImages.length === 0) ? (
                            <Placeholder />
                        ) : (
                            thisImages.filter(Boolean).slice(0, 2).map((image, idx) => (
                                <ImageThumbnail
                                    key={idx}
                                    src={image.imageUrl}
                                    alt={image.altName || image.name}
                                />
                            ))
                        )}
                        
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: 'var(--primary-color)', 
                                fontWeight: 'bold',
                                mx: 0.5
                            }}
                        >
                            →
                        </Typography>
                        
                        {(!thenImages || thenImages.length === 0) ? (
                            <Placeholder />
                        ) : (
                            thenImages.filter(Boolean).slice(0, 2).map((image, idx) => (
                                <ImageThumbnail
                                    key={idx}
                                    src={image.imageUrl}
                                    alt={image.altName || image.name}
                                />
                            ))
                        )}
                    </Box>
                    
                    <Tooltip title="Delete" placement="top">
                        <DeleteButton 
                            className="delete-button" 
                            onClick={handleDelete}
                            size="small"
                            aria-label="delete this-then item"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                width: 20,
                                height: 20,
                                minWidth: 'auto'
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                        </DeleteButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </StyledCard>
    );
};

export default DraggableThisThenItem;
